import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../shared/config";
import { useAuth } from "../../contexts/AuthContext";
import { getPhotoUrl, PhotoWithFallback } from "../../shared/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Users, LogOut, X, Building2, Menu, MoreHorizontal, ChevronDown, Command, LayoutDashboard } from "lucide-react";
import logoOp from "../../assets/logo-op.png";
import Raccourcis from "../Raccourcis";
import { ChatNavButton } from "./ChatNavButton";
import { BoutonDoctiproNav } from "../shared/PatientDoctiproCard";
import { ModeJourNuit } from "./ModeJourNuit";
import { estDirecteur } from '../../lib/droits';
import { VuePerimetreToggle } from '../shared/VuePerimetreToggle';
import { demanderDeconnexion } from '../../lib/pointage';
import "../../navigation/nav.css";
import "../../navigation/futurSkin.css";

// Logo en 2 boutons : « OP » ouvre/ferme la page d'accueil, « OphtaPilot » ramène aux actualités
const LogoBoutons = ({ accueilActif, onToggleAccueil, onActualites, size = 'w-9 h-9', titre = true }) => (
  <div className="flex items-center gap-1 shrink-0">
    <button type="button" onClick={onToggleAccueil} aria-pressed={accueilActif}
      title={accueilActif ? 'Revenir aux actualités' : "Page d'accueil"}
      className={`opn-logo-op shrink-0 p-1 rounded-xl transition-all duration-200 hover:bg-[#EAF2FC] hover:scale-105 ${accueilActif ? 'bg-[#EAF2FC] ring-2 ring-[#1173D4]/40' : ''}`}
      data-testid="nav-logo-op-btn">
      <img src={logoOp} alt="OP" className={`${size} shrink-0 object-contain`} />
    </button>
    {titre && (
      <button type="button" onClick={onActualites} title="Actualités"
        className="opn-logo-titre px-2 py-1 rounded-xl text-lg font-bold text-gray-800 hover:bg-[#EAF2FC] transition-colors hidden sm:block"
        data-testid="nav-logo-titre-btn">
        Ophta<span className="text-[#1173D4]">Pilot</span>
      </button>
    )}
  </div>
);

// Bouton « Mon espace » : poste de travail du rôle, pastille = éléments à traiter
const BoutonEspace = ({ actif, badge, onClick, compact = false }) => (
  <button type="button" onClick={onClick} aria-pressed={actif} title="Mon espace de travail" data-testid="nav-mon-espace-btn"
    className={`relative shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-full font-bold text-sm transition-all duration-200 ${compact ? 'px-3 py-1.5' : 'px-4 py-2'} ${actif ? 'bg-[#1173D4] text-white shadow-lg shadow-[#1173D4]/30 scale-[1.02]' : 'bg-[#0A2350] text-white hover:bg-[#123a7a] shadow-md shadow-[#0A2350]/25'}`}>
    <LayoutDashboard className="h-4 w-4" />
    <span className={compact ? '' : 'hidden sm:inline'}>Mon espace</span>
    {badge > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-extrabold grid place-items-center border-2 border-white" data-testid="nav-mon-espace-badge">{badge > 99 ? '99+' : badge}</span>}
  </button>
);

const Navigation = ({ menuOpen, setMenuOpen, menuItems, activeTab, setActiveTab, onOpenCmdK, onRaccourciAction, accueilActif, onToggleAccueil, onActualites, espaceBadge = 0, onEspace }) => {
  const { user, logout, setUser, centres, centreActif, switchCentre } = useAuth();
  const isSuperAdmin = estDirecteur(user, 'admin');

  const handleStopImpersonation = async () => {
    try {
      const originalToken = localStorage.getItem('originalToken');
      if (originalToken) {
        // Restaurer le token original
        localStorage.setItem('token', originalToken);
        localStorage.removeItem('originalToken');
        localStorage.removeItem('isImpersonating');
        
        // Mettre à jour l'en-tête d'autorisation d'axios
        axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
        
        // Récupérer les infos du directeur
        const response = await axios.get(`${API}/users/me`);
        setUser(response.data);
        
        toast.success('Retour à votre compte directeur');
        
        // Rafraîchir la page pour éviter les problèmes de state
        window.location.reload();
      }
    } catch (error) {
      toast.error('Erreur lors du retour au compte directeur');
    }
  };

  const getInitials = (nom, prenom) => {
    return `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Directeur': return 'bg-gradient-to-br from-red-500 to-red-600';
      case 'Manager': return 'bg-gradient-to-br from-orange-500 to-orange-600';
      case 'Médecin': return 'bg-gradient-to-br from-[#1173D4] to-[#0A2350]';
      case 'Assistant': return 'bg-gradient-to-br from-[#19CD91] to-[#14A474]';
      case 'Secrétaire': return 'bg-gradient-to-br from-purple-500 to-purple-600';
      default: return 'bg-gradient-to-br from-gray-400 to-gray-500';
    }
  };

  // Séparer les items en deux catégories : items généraux (visibles dans la barre) et items admin (dans "Plus")
  // Les items admin sont : Administration et Gestion Centres
  const adminItemIds = ['admin', 'centres', 'autorisations', 'plan-cabinet', 'salles', 'notifications-admin', 'rgpd', 'pare-feu', 'bornes', 'autres-parametres'];
  const mainMenuItems = menuItems.filter(item => !adminItemIds.includes(item.id));
  const moreMenuItems = menuItems.filter(item => adminItemIds.includes(item.id));

  return (
    <>
      {/* Navbar principale */}
      <nav className="opn-legacy-nav bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-12">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              {/* Bouton Menu Hamburger - MOBILE ONLY */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden p-2.5 rounded-xl text-gray-500 hover:text-[#1173D4] hover:bg-[#EAF2FC] transition-all duration-200"
                aria-label="Menu"
                data-testid="menu-toggle-btn"
              >
                <Menu className="h-5 w-5" />
              </button>
              
              <LogoBoutons accueilActif={accueilActif} onToggleAccueil={onToggleAccueil} onActualites={onActualites} />
              {onEspace && <BoutonEspace actif={activeTab === 'espace'} badge={espaceBadge} onClick={onEspace} />}
              
              {/* Sélecteur de centre (visible si l'utilisateur a accès à plusieurs centres) */}
              {centres.length > 0 && (
                <div className="hidden md:block">
                  <Select 
                    value={centreActif?.id || ''} 
                    onValueChange={(value) => switchCentre(value)}
                    disabled={centres.length === 1}
                  >
                    <SelectTrigger 
                      className={`h-9 w-[180px] border-[#1173D4]/30 bg-[#EAF2FC]/50 text-sm ${centres.length === 1 ? 'cursor-default' : ''}`}
                      data-testid="centre-switcher"
                    >
                      <Building2 className="h-4 w-4 mr-2 text-[#1173D4]" />
                      <SelectValue placeholder="Sélectionner un centre" />
                    </SelectTrigger>
                    {centres.length > 1 && (
                      <SelectContent>
                        {centres.map((centre) => (
                          <SelectItem key={centre.id} value={centre.id}>
                            {centre.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    )}
                  </Select>
                </div>
              )}
              <BoutonDoctiproNav />
              <ChatNavButton className="hidden sm:block" />
              <Raccourcis
                raccourcis={user?.raccourcis || []}
                user={user}
                onAction={onRaccourciAction}
                onUpdate={(ids) => setUser?.({ ...user, raccourcis: ids })}
              />
            </div>

            <div className="flex-1" />

            <div className="flex items-center space-x-3">
              <VuePerimetreToggle className="hidden sm:inline-flex" />
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10 ring-2 ring-white shadow-md cursor-pointer hover:ring-[#1173D4] transition-all" onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'profil' }))} title="Mon Profil" data-testid="avatar-profile-link">
                  {user?.photo_url && <AvatarImage src={getPhotoUrl(user.photo_url)} alt={`${user?.prenom} ${user?.nom}`} />}
                  <AvatarFallback className={`${getRoleColor(user?.role)} text-white text-sm font-semibold`}>
                    {getInitials(user?.nom, user?.prenom)}
                  </AvatarFallback>
                </Avatar>
                <div className="text-sm hidden md:block">
                  <p className="font-semibold text-gray-800">
                    {user?.prenom} {user?.nom}
                  </p>
                  <Badge variant="secondary" className="text-xs font-medium bg-gray-100 text-gray-600 border-0">
                    {user?.role === 'Directeur' ? 'Directeur' : user?.role}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {localStorage.getItem('isImpersonating') === 'true' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStopImpersonation}
                    className="flex items-center space-x-1.5 bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 rounded-xl"
                    data-testid="stop-impersonation-btn"
                  >
                    <Users className="h-4 w-4" />
                    <span className="hidden sm:inline">Retour Directeur</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onOpenCmdK}
                  className={`h-9 w-9 text-gray-500 hover:text-[#1173D4] hover:bg-blue-50 dark:hover:bg-slate-800 dark:text-slate-300 rounded-xl ${isSuperAdmin ? '' : 'hidden'}`}
                  title="Recherche rapide (Ctrl+K)"
                  data-testid="open-cmdk-btn"
                >
                  <Command className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => demanderDeconnexion(logout)}
                  className="flex items-center space-x-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-slate-800 dark:text-slate-300 rounded-xl transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Sélecteur de centre mobile (visible si plusieurs centres) */}
        {centres.length > 1 && (
          <div className="md:hidden px-4 pb-2">
            <Select 
              value={centreActif?.id || ''} 
              onValueChange={(value) => switchCentre(value)}
            >
              <SelectTrigger className="w-full h-9 border-[#1173D4]/30 bg-[#EAF2FC]/50 text-sm">
                <Building2 className="h-4 w-4 mr-2 text-[#1173D4]" />
                <SelectValue placeholder="Sélectionner un centre" />
              </SelectTrigger>
              <SelectContent>
                {centres.map((centre) => (
                  <SelectItem key={centre.id} value={centre.id}>
                    {centre.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </nav>

      {menuOpen && (
        <>
          {/* Overlay fond */}
          <div className="lg:hidden fixed inset-0 bg-black/30 z-40" onClick={() => setMenuOpen(false)} />
          {/* Menu mobile plein écran */}
          <div className="lg:hidden fixed left-0 top-0 bottom-0 opn-legacy-drawer w-72 bg-white shadow-2xl z-50 animate-scale-in overflow-y-auto">
            <div className="p-4">
              {/* En-tête du menu */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b">
                <LogoBoutons accueilActif={accueilActif} size="w-8 h-8" titre={false}
                  onToggleAccueil={() => { onToggleAccueil(); setMenuOpen(false); }}
                  onActualites={() => { onActualites(); setMenuOpen(false); }} />
                <button type="button" onClick={() => { onActualites(); setMenuOpen(false); }} className="font-bold text-gray-800 px-2 py-1 rounded-lg hover:bg-[#EAF2FC]" data-testid="nav-mobile-logo-titre-btn">Ophta<span className="text-[#1173D4]">Pilot</span></button>
                <ModeJourNuit />
                <button onClick={() => setMenuOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="sm:hidden mb-3 flex justify-center" data-testid="mobile-vue-perimetre">
                <VuePerimetreToggle />
              </div>
              {onEspace && <div className="mb-3 flex justify-center"><BoutonEspace compact actif={activeTab === 'espace'} badge={espaceBadge} onClick={() => { onEspace(); setMenuOpen(false); }} /></div>}
              <nav className="space-y-1">
                {menuItems.map(item => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveTab(item.id);
                        setMenuOpen(false);
                      }}
                      data-testid={`nav-item-${item.id}`}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-[#1173D4] to-[#0A2350] text-white shadow-md shadow-[#1173D4]/25'
                          : 'text-gray-600 hover:text-[#1173D4] hover:bg-[#EAF2FC]'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>
                        <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </>
      )}

      {/* Barre de navigation horizontale - DESKTOP ONLY */}
      <div className="opn-legacy-bar hidden lg:block bg-white/80 backdrop-blur-sm border-b border-gray-100 fixed top-16 left-0 right-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-12">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center space-x-1">
              {mainMenuItems.map(item => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    data-testid={`nav-desktop-${item.id}`}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-[#1173D4] to-[#0A2350] text-white shadow-md'
                        : 'text-gray-600 hover:text-[#1173D4] hover:bg-[#EAF2FC]'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : ''}`} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
              
              {/* Menu "Plus" pour les items supplémentaires */}
              {moreMenuItems.length > 0 && (
                <div className="relative group">
                  <button className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-[#1173D4] hover:bg-[#EAF2FC] transition-all duration-200">
                    <MoreHorizontal className="h-4 w-4" />
                    <span>Plus</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  
                  {/* Dropdown menu */}
                  <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="p-2">
                      {moreMenuItems.map(item => {
                        const Icon = item.icon;
                        const isActive = activeTab === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            data-testid={`nav-more-${item.id}`}
                            className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                              isActive
                                ? 'bg-[#EAF2FC] text-[#1173D4]'
                                : 'text-gray-600 hover:text-[#1173D4] hover:bg-[#EAF2FC]'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


// Personnel Management Component

export default Navigation;
